import { useState } from "react";
import type { GcrResponse, GcrWeek } from "../types/contract";
import { putGcrMcConsents } from "../api/client";

/**
 * Google カスタマーレビュー（GCR）仕込みの小カード（2026-10-19・設計書 §7 P3）。
 * 表示数（同意ページを出した回数）／MC 同意数（手入力）／同意率（同意数 ÷ Shopify 注文数・判定 ≥ 20%）／
 * オーナー登録 LP への流入（utm_source=thankyou）を週次で見る。MC 同意数は API で取れないので週ごとに手入力する。
 */
interface GcrCardProps {
  data: GcrResponse;
  onChanged?: () => void;
}

const TARGET_RATE = 0.2;

function pct(v: number | null): string {
  return v == null ? "—" : `${(v * 100).toFixed(1)}%`;
}
function n(v: number | null): string {
  return v == null ? "—" : v.toLocaleString("ja-JP");
}

export function GcrCard({ data, onChanged }: GcrCardProps) {
  const t = data.totals;
  const rateOk = t.consent_rate != null && t.consent_rate >= TARGET_RATE;
  return (
    <section className="card" aria-label="Google カスタマーレビュー">
      <h2 className="section-title">Google カスタマーレビュー（購入完了ブロック）</h2>
      <div className="gcr-kpis">
        <div className="gcr-kpi">
          <span className="gcr-kpi-label">同意ページ表示</span>
          <b>{n(t.shown)}</b>
          <span className="cell-sub">期限切れ {n(t.expired)}／取得不可 {n(t.unavailable)}</span>
        </div>
        <div className="gcr-kpi">
          <span className="gcr-kpi-label">MC 同意数（手入力）</span>
          <b>{n(t.mc_consents)}</b>
          <span className="cell-sub">Shopify 注文 {n(t.shopify_orders)} 件</span>
        </div>
        <div className="gcr-kpi">
          <span className="gcr-kpi-label">同意率（目標 20%）</span>
          <b style={{ color: t.consent_rate == null ? undefined : rateOk ? "var(--st-active-c)" : "var(--warn)" }}>{pct(t.consent_rate)}</b>
          <span className="cell-sub">同意数 ÷ 注文数</span>
        </div>
        <div className="gcr-kpi">
          <span className="gcr-kpi-label">オーナー登録LP 流入</span>
          <b>{n(t.owner_pv_thankyou)}</b>
          <span className="cell-sub">/pages/owner・utm_source=thankyou（目標 週5件）</span>
        </div>
      </div>
      {!data.sources.events && <p className="cell-sub">表示ログ（gcr_optin_events）が未作成です。migration 0090 を当てると出ます。</p>}
      <div className="table-wrap" style={{ marginTop: 12 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>週</th>
              <th>表示</th>
              <th>期限切れ</th>
              <th>LP流入(thankyou)</th>
              <th>Shopify注文</th>
              <th>MC同意数（手入力）</th>
              <th>同意率</th>
            </tr>
          </thead>
          <tbody>
            {data.weekly.map((w) => (
              <GcrWeekRow key={w.week_start} w={w} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GcrWeekRow({ w, onChanged }: { w: GcrWeek; onChanged?: () => void }) {
  const [value, setValue] = useState<string>(w.mc_consents == null ? "" : String(w.mc_consents));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const rate = w.mc_consents != null && w.shopify_orders ? w.mc_consents / w.shopify_orders : null;
  const dirty = value !== (w.mc_consents == null ? "" : String(w.mc_consents));

  async function save() {
    const num = Number(value);
    if (!Number.isInteger(num) || num < 0) {
      setErr("0以上の整数");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await putGcrMcConsents(w.week_start, num);
      onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr>
      <td>{w.label}</td>
      <td>{n(w.shown)}</td>
      <td>{n(w.expired)}</td>
      <td>{n(w.owner_pv_thankyou)}</td>
      <td>{n(w.shopify_orders)}</td>
      <td>
        <input
          className="input"
          type="number"
          min={0}
          inputMode="numeric"
          style={{ width: 72 }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label={`${w.label} の MC 同意数`}
        />{" "}
        <button type="button" className="btn" disabled={!dirty || saving} onClick={save}>
          {saving ? "保存中…" : "保存"}
        </button>
        {err && <span className="cell-sub" style={{ color: "var(--warn)", marginLeft: 6 }}>{err}</span>}
      </td>
      <td>{pct(rate)}</td>
    </tr>
  );
}
